import { Stack, Tab, Tabs } from '@mui/material';
import React, { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view';

// This offset is used to determine how close from the top of the section
// is to the top of the page before we determine that this is the tab to highlight.
const CONFIG_MAIN_CONTENT_OFFSET = 150;
// Controls how fast we scroll to the tabs users click on the tab.
const SCROLL_DURATION = 100;

interface AdGroupTabsProps {
  tabValues: object;
  sectionRefs: MutableRefObject<{}>;
  activeTab: string;
}

function AdGroupTabs({ activeTab, tabValues, sectionRefs }: AdGroupTabsProps) {
  const [loaded, setLoaded] = useState(false);
  const tabKeys = Object.keys(tabValues);
  const defaultTab = tabKeys[0];
  const lastTab = tabKeys[tabKeys.length - 1];
  const navigate = useNavigate();

  useEffect(() => {
    const setActiveTab = (tab) => {
      navigate(`#${tab}`);
    };
    let scrollTimeoutId;

    const handleScroll = () => {
      // Put a delay here before adding the event handler for scrolling.
      // If user clicks on a new ad group with, eg #REPORTING, give it time to auto-scroll to that section first.
      // Then add this event handler to handle user scrolling.
      const configMainContentElement = document.getElementById('config-main-content');
      if (!configMainContentElement) {
        return;
      }
      scrollTimeoutId = setTimeout(() => {
        const { scrollTop, scrollHeight, offsetHeight } = configMainContentElement;
        if (scrollHeight - scrollTop <= offsetHeight) {
          setActiveTab(lastTab);
          return;
        }
        let tab = '';
        const sectionRefKeys = Object.keys(sectionRefs.current);
        sectionRefKeys.forEach((key, index) => {
          const section = sectionRefs.current[key];
          if (section && section.offsetTop - CONFIG_MAIN_CONTENT_OFFSET <= scrollTop) {
            tab = key;
          }
          if (tab && index >= sectionRefKeys.length - 1) {
            setActiveTab(tab);
          }
        });
        // Double the amount of time it takes for the scroll to happen
        // before we calculate the selected tab to highlight.
      }, SCROLL_DURATION * 2);
    };
    document.getElementById('config-main-content')?.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      document.getElementById('config-main-content')?.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
    };
  }, [sectionRefs, tabKeys, tabValues, lastTab, navigate]);

  const handleTabChange = useCallback(
    (newTab: string) => {
      if (newTab) {
        // When scrolling to the last tab in smooth behavior, it is too slow to complete, causing
        // the event handler on the scroll event to trigger and think we are still on the 2nd last section,
        // and trigger a jump back to the second last tab unless only the user scrolls all the way to the bottom.
        scrollIntoView(sectionRefs.current[newTab], {
          time: newTab === lastTab ? 0 : SCROLL_DURATION,
          align: { top: 0 },
        });
      }
    },
    [sectionRefs, lastTab]
  );

  useEffect(() => {
    // We only want to jump to the correct tab on page load
    if (!loaded) {
      handleTabChange(activeTab);
      setLoaded(true);
    }
  }, [handleTabChange, activeTab, loaded]);

  return (
    <Stack
      sx={{
        borderBottom: '1px solid rgb(0, 0, 0, 0.2)',
      }}
    >
      <Tabs value={activeTab || defaultTab} aria-label="ad group tab navigation" variant="fullWidth">
        {Object.keys(tabValues).map((key) => (
          <Tab
            key={key}
            value={key}
            label={tabValues[key]}
            onClick={() => handleTabChange(key)}
            sx={{ textTransform: 'capitalize' }}
          />
        ))}
      </Tabs>
    </Stack>
  );
}

export default AdGroupTabs;
